import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { QRCodeSVG } from "qrcode.react";

// ---------- Print CSS (page breaks + hide UI on print) ----------
const printCss = `
@media print {
  .no-print { display: none !important; }
  body { background: white; }
  .page-break { page-break-before: always; }
}
`;

// ---------- Amount in words (Indian numbering) ----------
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
function twoDigitToWords(n) {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10), o = n % 10;
  return (TENS[t] + (o ? " " + ONES[o] : "")).trim();
}
function threeDigitToWords(n) {
  const h = Math.floor(n / 100), rest = n % 100;
  const parts = [];
  if (h) parts.push(ONES[h] + " Hundred");
  if (rest) parts.push(twoDigitToWords(rest));
  return parts.join(" ").trim();
}
export function amountToWords(num) {
  num = Math.round(Number(num) || 0);
  if (num === 0) return "Zero Rupees Only";
  const crore = Math.floor(num / 10000000);
  const lakh  = Math.floor((num % 10000000) / 100000);
  const thou  = Math.floor((num % 100000) / 1000);
  const hun   = num % 1000;
  const parts = [];
  if (crore) parts.push(twoDigitToWords(crore) + " Crore");
  if (lakh)  parts.push(twoDigitToWords(lakh)  + " Lakh");
  if (thou)  parts.push(twoDigitToWords(thou)  + " Thousand");
  if (hun)   parts.push(threeDigitToWords(hun));
  return parts.filter(Boolean).join(" ") + " Rupees Only";
}

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

// ---------- Freight Basis row (shows Per MT / Per Vehicle / Fixed rate math on LR) ----------
const FREIGHT_LABELS = {
  lumpsum: "Lumpsum (Fixed Freight)",
  per_mt: "Per Metric Tonne",
  per_kg: "Per Kg",
  per_km: "Per Km",
  per_machine: "Per Machine",
  per_bus: "Per Bus",
  per_car: "Per Car",
  per_truck: "Per Truck / Per Vehicle",
  per_unit: "Per Unit",
};
function FreightBasisRow({ trip }) {
  const fr = trip.freight || {};
  const type = fr.type || "lumpsum";
  const label = FREIGHT_LABELS[type] || type;
  const unitPrice = Number(fr.unit_price || 0);
  const units = Number(fr.units || 0);
  const total = Number(trip.money?.customer_freight || 0);
  const isUnit = type !== "lumpsum" && unitPrice > 0 && units > 0;
  return (
    <div className="grid grid-cols-2 border-b border-slate-400 divide-x divide-slate-300">
      <div className="p-3 text-[12px]">
        <span className="text-slate-600 mr-2">Freight Basis:</span>
        <span className="font-semibold">{label}</span>
      </div>
      <div className="p-3 text-[12px] text-right">
        {isUnit ? (
          <span>
            <span className="font-semibold font-mono">{inr(unitPrice)}</span>
            <span className="text-slate-500"> × </span>
            <span className="font-semibold font-mono">{units}</span>
            <span className="text-slate-500"> = </span>
            <span className="font-bold font-mono text-[#1e3a8a]" data-testid="lr-freight-total">{inr(unitPrice * units)}</span>
          </span>
        ) : (
          <span>
            <span className="text-slate-600 mr-2">Total Freight:</span>
            <span className="font-bold font-mono text-[#1e3a8a]" data-testid="lr-freight-total">{inr(total)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

// ---------- Reusable pieces ----------
export function PrintFrame({ children }) {
  return (
    <div className="min-h-screen bg-white text-[#0f172a] p-6 sm:p-8" style={{ fontFamily: "'IBM Plex Sans', 'Inter', sans-serif" }}>
      <style>{printCss}</style>
      <div className="no-print max-w-[900px] mx-auto mb-4 flex justify-between items-center">
        <a href="javascript:history.back()" className="text-sm text-zinc-500 hover:text-[#FF5A00]">← Back</a>
        <button onClick={() => window.print()} data-testid="print-btn"
          className="px-4 py-2 bg-[#FF5A00] text-white rounded font-semibold">Print / Save PDF</button>
      </div>
      <div className="max-w-[900px] mx-auto bg-white border border-slate-400">{children}</div>
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex gap-2 text-[12px] leading-snug py-0.5">
      <div className="text-slate-600 min-w-[140px]">{label}</div>
      <div className={`text-slate-900 font-semibold ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function InvoiceHeader({ title, dateStr, trackUrl }) {
  return (
    <div className="p-5 border-b border-slate-400 flex items-start justify-between gap-4">
      <div className="flex items-start gap-4">
        <div className="scale-[1.4] origin-top-left">
          <Logo variant="light" showTagline />
        </div>
      </div>
      <div className="flex items-start gap-4">
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">DATE</div>
          <div className="text-sm font-semibold text-slate-900 font-mono">{dateStr}</div>
          <div className="text-[10px] text-slate-500 mt-2">{title}</div>
        </div>
        {trackUrl && (
          <div className="p-1.5 bg-white border border-slate-300" data-testid="invoice-qr-code">
            <QRCodeSVG value={trackUrl} size={70} level="M" bgColor="#ffffff" fgColor="#0f172a" />
          </div>
        )}
      </div>
    </div>
  );
}

function SignatureAndAddress({ tagline = "Please refer annexure for details of every consignment." }) {
  return (
    <div className="p-5 grid grid-cols-2 gap-6 border-t border-slate-300">
      <div>
        <div className="text-[11px] font-bold text-slate-900">For NORTHLINE LOGISTICS PRIVATE LIMITED</div>
        <div className="mt-10 border-t border-slate-500 pt-1 max-w-[180px]">
          <div className="text-[10px] uppercase tracking-widest text-slate-600">Authorised Signatory</div>
        </div>
        <div className="mt-4 text-[10px] text-slate-600">Declaration:</div>
        <div className="text-[10px] text-slate-600 italic">{tagline}</div>
      </div>
      <div className="text-right">
        <div className="text-[11px] font-bold text-slate-900">Registered Office:</div>
        <div className="text-[11px] text-slate-700 leading-relaxed mt-1">
          Northline Logistics Private Limited<br />
          #107 Plaza, Cloth Market, Nagori Gate,<br />
          Rajguru Market Road, Hisar,<br />
          Haryana 125001, India.
        </div>
        <div className="text-[10px] text-slate-600 mt-2">
          Helpline: +91 99966 61953 / 99966 61194<br />
          Dushyant.kumar@northline.co.in · Sandeep@northline.co.in
        </div>
      </div>
    </div>
  );
}

// ================= LORRY RECEIPT / CONSIGNMENT NOTE =================
export function PrintLR() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trips/${id}`);
        setTrip(data.trip);
      } catch (err) { /* ignore */ }
    })();
  }, [id]);
  if (!trip) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  const trackUrl = typeof window !== "undefined" ? `${window.location.origin}/track/${trip.cn_number}` : `/track/${trip.cn_number}`;
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });

  return (
    <PrintFrame>
      <InvoiceHeader title="Consignment Note / Lorry Receipt" dateStr={today} trackUrl={trackUrl} />

      <div className="grid grid-cols-2 border-b border-slate-400 divide-x divide-slate-400">
        <div className="p-5">
          <div className="text-[10px] uppercase text-slate-500 mb-1">Original</div>
          <div className="text-[11px] font-bold text-slate-900">CN Number</div>
          <div className="text-xl font-bold text-[#1e3a8a] font-mono" data-testid="lr-cn-number">{trip.cn_number}</div>
          <div className="mt-3">
            <div className="text-[11px] font-bold text-slate-900">Consignor:</div>
            <div className="text-sm font-semibold">{trip.customer?.consignor || trip.customer?.name || "—"}</div>
            {trip.customer?.consignor_address && <div className="text-[11px] text-slate-600 whitespace-pre-line">{trip.customer.consignor_address}</div>}
            {trip.customer?.phone && <div className="text-[11px] text-slate-600">Phone: {trip.customer.phone}</div>}
            <div className="text-[11px] text-slate-600">GSTIN: <span className="font-mono">{trip.customer?.consignor_gstin || trip.customer?.gstin || "Not applicable"}</span></div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200">
            <div className="text-[11px] font-bold text-slate-900">Consignee:</div>
            <div className="text-sm font-semibold">{trip.customer?.consignee || "—"}</div>
            {trip.customer?.consignee_address
              ? <div className="text-[11px] text-slate-600 whitespace-pre-line">{trip.customer.consignee_address}</div>
              : <div className="text-[11px] text-slate-600">{trip.destination_city}, India</div>}
            <div className="text-[11px] text-slate-600">GSTIN: <span className="font-mono">{trip.customer?.consignee_gstin || "Not applicable"}</span></div>
          </div>
        </div>
        <div className="p-5">
          <KV label="Trip Type:" value={trip.trip_type === "driver_placement" ? "Driver Placement" : "Vehicle Placement"} />
          <KV label="Pickup Date:" value={trip.pickup_date || "—"} />
          <KV label="Delivery Date:" value={trip.delivery_date || "—"} />
          <KV label="Sales Order #:" value={trip.sales_order_number || "—"} mono />
          <KV label="Customer Invoice #:" value={trip.invoice_number || "—"} mono />
          <KV label="E-way Bill #:" value={trip.eway_bill_number || "—"} mono />
          <KV label="E-way Expiry:" value={trip.eway_bill_expiry || "—"} />
          <KV label="Place of Supply:" value={trip.destination_city || "—"} />
        </div>
      </div>

      <div className="grid grid-cols-3 border-b border-slate-400 bg-slate-50 divide-x divide-slate-300">
        <div className="p-3 text-[12px]"><span className="text-slate-600 mr-2">Mode of Service:</span><span className="font-semibold">By Road</span></div>
        <div className="p-3 text-[12px]"><span className="text-slate-600 mr-2">Origin:</span><span className="font-semibold">{trip.source_city}</span></div>
        <div className="p-3 text-[12px]"><span className="text-slate-600 mr-2">Destination:</span><span className="font-semibold">{trip.destination_city}</span></div>
      </div>

      <div className="grid grid-cols-2 border-b border-slate-400 divide-x divide-slate-300">
        <div className="p-4">
          <div className="text-[11px] font-bold text-slate-900 mb-1">VEHICLE</div>
          <KV label="Vehicle Number:" value={trip.vehicle?.vehicle_number || "—"} mono />
          <KV label="Owner:" value={trip.vehicle?.owner === "northline" ? "Northline Fleet" : (trip.vehicle?.lorry_owner_name || "Outsourced")} />
        </div>
        <div className="p-4">
          <div className="text-[11px] font-bold text-slate-900 mb-1">DRIVER</div>
          <KV label="Driver Name:" value={trip.driver?.name || "—"} />
          <KV label="DL Number:" value={trip.driver?.dl_number || "—"} mono />
          <KV label="Driver Phone:" value={trip.driver?.phone || "—"} mono />
        </div>
      </div>

      <table className="w-full text-[12px] border-b border-slate-400">
        <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-700 font-bold">
          <tr>
            <th className="text-left p-2 border-r border-slate-300 w-[55%]">PARTICULARS</th>
            <th className="text-right p-2 border-r border-slate-300">MATERIAL</th>
            <th className="text-right p-2">WEIGHT (KG)</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-300 align-top">
            <td className="p-2 border-r border-slate-300">
              <div className="font-semibold">Freight — {trip.source_city} to {trip.destination_city}</div>
              <div className="text-[11px] text-slate-600 mt-1">Consignment moved by Northline Logistics under CN {trip.cn_number}.</div>
            </td>
            <td className="p-2 text-right border-r border-slate-300">{trip.material || "—"}</td>
            <td className="p-2 text-right font-mono">{trip.weight_kg || 0}</td>
          </tr>
        </tbody>
      </table>

      <FreightBasisRow trip={trip} />

      <div className="p-4 border-b border-slate-300 text-[11px] text-slate-700 flex items-center justify-between gap-6 bg-slate-50">
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-1">Scan to Track this Shipment</div>
          <div>
            Scan the QR with any phone camera to view live status, POD outcome and full timeline for
            consignment <span className="font-mono font-bold text-[#1e3a8a]">{trip.cn_number}</span>.
          </div>
          <div className="text-[10px] text-slate-500 mt-1 font-mono break-all" data-testid="lr-track-url">{trackUrl}</div>
        </div>
        <div className="p-1.5 bg-white border border-slate-300" data-testid="lr-qr-code">
          <QRCodeSVG value={trackUrl} size={110} level="M" bgColor="#ffffff" fgColor="#0f172a" />
        </div>
      </div>

      <SignatureAndAddress tagline="Driver-cum-Consignee acknowledgment of goods received in good condition." />
    </PrintFrame>
  );
}

// ================= TAX INVOICE (Linkit-style) =================
export function PrintBill() {
  const { id } = useParams();
  const [bill, setBill] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get(`/bills/${id}`); setBill(data.bill); } catch (e) {} })(); }, [id]);
  if (!bill) return <div className="p-10 text-center text-slate-500">Loading…</div>;

  const billDate = new Date(bill.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
  const trips = bill.trips || [];
  const originSet = [...new Set(trips.map(t => t.source_city).filter(Boolean))];
  const destSet   = [...new Set(trips.map(t => t.destination_city).filter(Boolean))];
  const totalWeight = trips.reduce((s, t) => s + Number(t.weight_kg || 0), 0);
  const freightPerTrip = trips.length ? Math.round(bill.subtotal / trips.length) : 0;

  return (
    <PrintFrame>
      <InvoiceHeader title="Tax Invoice / Original" dateStr={billDate} />

      <div className="grid grid-cols-2 border-b border-slate-400 divide-x divide-slate-400">
        {/* Bill From */}
        <div className="p-5">
          <div className="text-[10px] uppercase text-slate-500">Original</div>
          <div className="mt-1 text-[12px] font-bold text-slate-900">Bill From:</div>
          <div className="text-sm font-bold text-slate-900">Northline Logistics Private Limited</div>
          <div className="text-[11px] text-slate-600">India</div>
          <div className="text-[11px] text-slate-600">GSTIN No.: Not applicable</div>

          <div className="mt-4 space-y-1">
            <div className="flex justify-between text-[12px]">
              <span className="font-bold text-slate-900">Invoice Number</span>
              <span className="font-mono text-slate-900" data-testid="bill-invoice-no">{bill.bill_no}</span>
            </div>
            <div className="flex justify-between text-[12px]">
              <span className="font-bold text-slate-900">Invoice Date</span>
              <span className="font-mono text-slate-900">{billDate}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-300">
            <div className="text-[12px] font-bold text-slate-900">Billing to:</div>
            <div className="text-sm font-bold text-slate-900">{bill.customer_name}.</div>
            <div className="text-[11px] text-slate-600 whitespace-pre-line">{bill.customer_address || "India"}</div>
            <div className="text-[11px] text-slate-600">GSTIN: {bill.customer_gstin || "—"}</div>
          </div>
        </div>

        {/* Order details */}
        <div className="p-5 text-[12px] space-y-1">
          <KV label="Order Date:" value={trips[0]?.pickup_date || billDate} />
          <KV label="Order Received by:" value="Northline Ops" />
          <KV label="Internal Bill Number:" value={bill.bill_no} mono />
          <KV label="Delivery Order Number:" value={trips.map(t => t.cn_number).slice(0, 3).join(", ") + (trips.length > 3 ? " +" + (trips.length - 3) : "")} mono />
          <KV label="Delivery Order Date:" value={trips[0]?.delivery_date || "—"} />
          <KV label="Insurance:" value="Consignor" />
          <KV label="Freight:" value="To Pay" />
          <KV label="Place of Supply:" value={destSet[0] || "—"} />
        </div>
      </div>

      {/* Service band */}
      <div className="grid grid-cols-3 border-b border-slate-400 bg-slate-50 divide-x divide-slate-300">
        <div className="p-3 text-[12px]"><span className="text-slate-600 mr-2">Mode of Service:</span><span className="font-semibold">By Road</span></div>
        <div className="p-3 text-[12px]"><span className="text-slate-600 mr-2">Origin:</span><span className="font-semibold">{originSet.join(", ") || "—"}</span></div>
        <div className="p-3 text-[12px]"><span className="text-slate-600 mr-2">Destination:</span><span className="font-semibold">{destSet.join(", ") || "—"}</span></div>
      </div>

      {/* Particulars */}
      <table className="w-full text-[12px] border-b border-slate-400">
        <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-700 font-bold">
          <tr>
            <th className="text-left p-2 border-r border-slate-300 w-[42%]">PARTICULARS</th>
            <th className="text-right p-2 border-r border-slate-300">FREIGHT PER TRIP</th>
            <th className="text-right p-2 border-r border-slate-300">QTY</th>
            <th className="text-right p-2 border-r border-slate-300">UOM</th>
            <th className="text-right p-2 border-r border-slate-300">EXTRA CHARGES</th>
            <th className="text-right p-2">AMOUNT (INR)</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-300 align-top">
            <td className="p-2 border-r border-slate-300">
              <div className="font-semibold">Road freight — consignments moved by Northline Logistics.</div>
              <div className="text-[11px] text-slate-600 mt-1">
                From <b>{originSet.join(", ") || "—"}</b> to <b>{destSet.join(", ") || "—"}</b>. Total weight moved: {totalWeight.toLocaleString("en-IN")} kg.
                Refer annexure for CN-wise breakdown.
              </div>
            </td>
            <td className="p-2 text-right border-r border-slate-300 font-mono">{inr(freightPerTrip)}</td>
            <td className="p-2 text-right border-r border-slate-300">{trips.length}</td>
            <td className="p-2 text-right border-r border-slate-300">TRIP</td>
            <td className="p-2 text-right border-r border-slate-300 font-mono">₹0</td>
            <td className="p-2 text-right font-mono font-bold">{inr(bill.subtotal)}</td>
          </tr>
        </tbody>
      </table>

      {/* Financial summary */}
      <div className="grid grid-cols-2 border-b border-slate-400">
        <div className="p-4 text-[11px] text-slate-700 border-r border-slate-300 space-y-1">
          <div className="font-bold text-slate-900">Bank Details:</div>
          <div>Beneficiary: Northline Logistics Private Limited</div>
          <div>Please quote invoice number <span className="font-mono text-slate-900">{bill.bill_no}</span> on any transfer.</div>
        </div>
        <div className="p-4">
          <div className="flex justify-between border-b border-slate-300 py-1 text-[12px]"><span className="font-semibold">SUB TOTAL</span><span className="font-mono font-semibold">{inr(bill.subtotal)}</span></div>
          <div className="flex justify-between border-b border-slate-300 py-1 text-[12px]"><span>GST @ {bill.tax_percent}%</span><span className="font-mono">{Number(bill.tax) > 0 ? inr(bill.tax) : "Not Applicable"}</span></div>
          <div className="flex justify-between py-2 bg-slate-100 px-2 text-[13px] font-bold border-t border-slate-400 mt-1"><span>TOTAL INVOICE AMOUNT</span><span className="font-mono text-[#1e3a8a]" data-testid="bill-total">{inr(bill.total)}</span></div>
          {Number(bill.amount_paid) > 0 && (
            <>
              <div className="flex justify-between text-[11px] text-slate-600 mt-1"><span>Paid</span><span className="font-mono">{inr(bill.amount_paid)}</span></div>
              <div className="flex justify-between text-[11px] text-slate-600"><span>Balance Due</span><span className="font-mono font-bold">{inr(bill.balance)}</span></div>
            </>
          )}
        </div>
      </div>

      <div className="p-4 border-b border-slate-300 grid grid-cols-1 gap-2">
        <div className="text-[12px]"><span className="font-bold text-slate-900">AMOUNT IN WORDS:</span> <span className="italic" data-testid="bill-amount-words">{amountToWords(bill.total)}</span></div>
        <div className="text-[12px]"><span className="font-bold text-slate-900">TERMS OF PAYMENT:</span> 7 DAYS NET DUE FROM INVOICE DATE.</div>
      </div>

      <SignatureAndAddress />

      {/* Annexure page */}
      <div className="page-break border-t-4 border-[#FF5A00] mt-6">
        <div className="p-5 flex items-center justify-between border-b border-slate-400">
          <div className="scale-[1.4] origin-top-left">
            <Logo variant="light" showTagline />
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-[#1e3a8a]">Annexure-I</div>
            <div className="text-[11px] text-slate-500">Invoice {bill.bill_no} · {billDate}</div>
          </div>
        </div>

        <table className="w-full text-[11px]" data-testid="bill-annexure">
          <thead className="bg-slate-100 text-[9px] uppercase tracking-widest text-slate-700 font-bold">
            <tr>
              <th className="text-left p-2 border border-slate-300">Sr No</th>
              <th className="text-left p-2 border border-slate-300">CN Number</th>
              <th className="text-left p-2 border border-slate-300">Vehicle #</th>
              <th className="text-left p-2 border border-slate-300">Pickup Date</th>
              <th className="text-left p-2 border border-slate-300">Origin</th>
              <th className="text-left p-2 border border-slate-300">Destination</th>
              <th className="text-left p-2 border border-slate-300">Delivery Date</th>
              <th className="text-right p-2 border border-slate-300">Weight (kg)</th>
              <th className="text-right p-2 border border-slate-300">Freight</th>
              <th className="text-right p-2 border border-slate-300">Extra</th>
              <th className="text-right p-2 border border-slate-300">Total</th>
            </tr>
          </thead>
          <tbody>
            {trips.map((t, i) => (
              <tr key={t.id} className="align-top">
                <td className="p-2 border border-slate-300 font-mono">{i + 1}</td>
                <td className="p-2 border border-slate-300 font-mono">{t.cn_number}</td>
                <td className="p-2 border border-slate-300 font-mono">{t.vehicle_number || "—"}</td>
                <td className="p-2 border border-slate-300">{t.pickup_date || "—"}</td>
                <td className="p-2 border border-slate-300">{t.source_city}</td>
                <td className="p-2 border border-slate-300">{t.destination_city}</td>
                <td className="p-2 border border-slate-300">{t.delivery_date || "—"}</td>
                <td className="p-2 border border-slate-300 text-right">{Number(t.weight_kg || 0).toLocaleString("en-IN")}</td>
                <td className="p-2 border border-slate-300 text-right font-mono">{inr(t.customer_freight)}</td>
                <td className="p-2 border border-slate-300 text-right font-mono">₹0</td>
                <td className="p-2 border border-slate-300 text-right font-mono font-semibold">{inr(t.customer_freight)}</td>
              </tr>
            ))}
            <tr className="bg-slate-50">
              <td colSpan={7} className="p-2 border border-slate-300"></td>
              <td className="p-2 border border-slate-300 text-right font-bold">{totalWeight.toLocaleString("en-IN")}</td>
              <td className="p-2 border border-slate-300 text-right font-mono font-bold">{inr(bill.subtotal)}</td>
              <td className="p-2 border border-slate-300 text-right font-mono font-bold">₹0</td>
              <td className="p-2 border border-slate-300 text-right font-mono font-bold text-[#1e3a8a]">{inr(bill.subtotal)}</td>
            </tr>
          </tbody>
        </table>

        <div className="p-4 text-center text-[10px] text-slate-500 tracking-widest uppercase">--------- End of report ---------</div>
        <div className="p-4 flex justify-between text-[11px] border-t border-slate-300">
          <div>Invoice duly approved by :: <span className="font-bold">Sandeep Sharma</span></div>
          <div className="font-mono text-slate-500">{new Date().toLocaleString("en-IN")}</div>
        </div>
      </div>
    </PrintFrame>
  );
}

// ================= DRIVER PERFORMANCE CARD =================
export function PrintDriverCard() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get(`/drivers/${id}/performance`); setData(data); } catch (e) {} })(); }, [id]);
  if (!data) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  const d = data.driver, t = data.totals;
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });

  return (
    <PrintFrame>
      <InvoiceHeader title="Driver Performance Card" dateStr={today} />

      <div className="grid grid-cols-2 border-b border-slate-400 divide-x divide-slate-400">
        <div className="p-5">
          <div className="text-[11px] font-bold text-slate-900">Driver:</div>
          <div className="text-lg font-bold text-slate-900">{d.name}</div>
          <div className="mt-2 space-y-0.5">
            <KV label="DL Number:" value={d.profile?.dl_number || "—"} mono />
            <KV label="Home:" value={`${d.profile?.home_city || "—"}, ${d.profile?.home_state || "—"}`} />
            <KV label="Experience:" value={`${d.profile?.experience_years || 0} years`} />
            <KV label="Phone:" value={d.phone || "—"} mono />
          </div>
        </div>
        <div className="p-5">
          <KV label="Card Date:" value={today} />
          <KV label="Verification:" value={d.verified ? "Verified" : "Unverified"} />
          <KV label="Registered:" value={d.created_at ? new Date(d.created_at).toLocaleDateString("en-IN") : "—"} />
          <KV label="Rating:" value={`${t.rating || 0} / 5.0`} />
        </div>
      </div>

      <table className="w-full text-[12px] border-b border-slate-400">
        <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-700 font-bold">
          <tr>
            <th className="text-left p-2 border-r border-slate-300">METRIC</th>
            <th className="text-right p-2 border-r border-slate-300">TOTAL TRIPS</th>
            <th className="text-right p-2 border-r border-slate-300">DELIVERED</th>
            <th className="text-right p-2 border-r border-slate-300">ONGOING</th>
            <th className="text-right p-2 border-r border-slate-300">CANCELLED</th>
            <th className="text-right p-2 border-r border-slate-300">COMPLETION</th>
            <th className="text-right p-2">REVENUE</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-300">
            <td className="p-2 border-r border-slate-300 font-semibold">All-time totals</td>
            <td className="p-2 border-r border-slate-300 text-right font-mono">{t.trips_total}</td>
            <td className="p-2 border-r border-slate-300 text-right font-mono">{t.delivered}</td>
            <td className="p-2 border-r border-slate-300 text-right font-mono">{t.on_going}</td>
            <td className="p-2 border-r border-slate-300 text-right font-mono">{t.cancelled}</td>
            <td className="p-2 border-r border-slate-300 text-right font-mono">{t.completion_rate}%</td>
            <td className="p-2 text-right font-mono font-bold">{inr(t.revenue_inr)}</td>
          </tr>
        </tbody>
      </table>

      <div className="p-4 border-b border-slate-300">
        <div className="text-[11px] font-bold text-slate-900 mb-2">RECENT TRIPS</div>
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left py-1 px-2">CN #</th>
              <th className="text-left py-1 px-2">Route</th>
              <th className="text-left py-1 px-2">Status</th>
              <th className="text-left py-1 px-2">Date</th>
              <th className="text-right py-1 px-2">Hire</th>
            </tr>
          </thead>
          <tbody>
            {(data.recent_trips || []).map((r, i) => (
              <tr key={i} className="border-t border-slate-200">
                <td className="py-1 px-2 font-mono">{r.cn_number}</td>
                <td className="py-1 px-2">{r.route}</td>
                <td className="py-1 px-2 uppercase">{r.status}</td>
                <td className="py-1 px-2">{r.date || "—"}</td>
                <td className="py-1 px-2 text-right font-mono">{inr(r.driver_hire)}</td>
              </tr>
            ))}
            {(data.recent_trips || []).length === 0 && (
              <tr><td colSpan={5} className="py-3 text-center text-slate-400">No trips yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <SignatureAndAddress tagline="This card is auto-generated from live trip data at Northline." />
    </PrintFrame>
  );
}
